import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, CheckCircle2, Clock, RefreshCw, ShieldCheck, X } from 'lucide-react';
import type { Approval, ApprovalRequest, DestructiveOperation } from '../api/approvalRequests';
import {
  approvalRequestKeys,
  useApprovalRequest,
  useApprovalRequests,
  useCreateApprovalRequest,
  useOpenApprovalRequestWith,
  useWithdrawApprovalRequest,
} from '../hooks/useApprovalRequests';
import {
  REASONS_BY_OPERATION,
  describeOperation,
  effectiveStatus,
  expiryCountdown,
  findOpenRequest,
  statusChip,
} from '../pages/approvalRequests.logic';
import { useAuth } from './AuthProvider';
import { ReasonActionBar, StatusChip } from './ui';

export interface ApprovalRequestModalProps {
  operation: DestructiveOperation;
  /** Content id, experiment id or CDN key the operation acts on. */
  targetId: string;
  /** What the approver sees: route path, experiment name, file key. */
  targetLabel: string;
  /** Heading; defaults to "<verb> <targetLabel>". */
  title?: string;
  /** Extra context shown above the reason picker, e.g. why a save needs approval. */
  description?: string;
  /**
   * Runs the approved call with the approval headers once a second operator
   * has approved. Must throw on failure so the modal can show it. It gets the
   * request too: a revision is published by the ids the SERVER put on the
   * request, so the call never depends on anything this page remembered.
   */
  execute: (approval: Approval, request: ApprovalRequest) => Promise<void>;
  /**
   * DM-7214: open the request some other way than POST /approval-requests.
   * Proposing a content revision stores the edit and opens its request in one
   * server call; that call goes here.
   */
  openRequest?: (reason: string, note: string | undefined) => Promise<ApprovalRequest>;
  /**
   * DM-7214: resume exactly this request (the one the server recorded on the
   * revision) instead of looking for an open one by operation + target.
   * `null` means there is none to resume: start at the reason picker.
   */
  resumeRequestId?: string | null;
  /** DM-7214: the live route moved on; the approved content can no longer be published. */
  stale?: boolean;
  /** DM-7214: reopen the stored text in the editor (rebase, or revise after a rejection). */
  onRevise?: () => void;
  onClose: () => void;
  /** Fired once execute() has succeeded, before the modal closes itself. */
  onCompleted: () => void;
}

type ExecutionState = 'idle' | 'running' | 'done' | 'failed';

const DESTRUCTIVE_OPERATIONS: readonly DestructiveOperation[] = [
  'deleteContent',
  'deleteCdnFile',
  'archiveExperiment',
];

/**
 * DM-6437: the one place a console action asks for FM-105 dual approval.
 *
 * Step 1 records an enumerated reason and opens an approval request (or
 * resumes one already open for this operation + target). Step 2 waits,
 * polling, while a second operator decides it on the Approvals page. Step 3
 * runs the original call with the reason and the server-minted token the
 * moment the request comes back approved, exactly once, and reports the
 * outcome in place. Nobody types a token.
 */
export default function ApprovalRequestModal({
  operation,
  targetId,
  targetLabel,
  title,
  description,
  execute,
  openRequest,
  resumeRequestId,
  stale = false,
  onRevise,
  onClose,
  onCompleted,
}: ApprovalRequestModalProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [requestId, setRequestId] = useState<string | null>(resumeRequestId ?? null);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [execution, setExecution] = useState<ExecutionState>('idle');
  const [executionError, setExecutionError] = useState<string | null>(null);
  // Bumped by "Try again" so the completion effect re-runs against the same request.
  const [attempt, setAttempt] = useState(0);

  // The requests this modal has already executed for, so a poll that lands
  // between "execute started" and "server marked COMPLETED" cannot run the
  // action twice. Held in a ref: the guard must hold across renders without
  // being part of the effect's dependency list.
  const executedFor = useRef<string | null>(null);
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  const onCompletedRef = useRef(onCompleted);
  useEffect(() => {
    onCompletedRef.current = onCompleted;
  });

  const openRequests = useApprovalRequests();
  const detail = useApprovalRequest(requestId);
  const createMutation = useCreateApprovalRequest();
  const openMutation = useOpenApprovalRequestWith(openRequest);
  const withdrawMutation = useWithdrawApprovalRequest();

  const nowIso = new Date().toISOString();
  const operationInfo = describeOperation(operation);
  const heading = title ?? `${operationInfo.verb} ${targetLabel}`;
  const destructive = DESTRUCTIVE_OPERATIONS.includes(operation);

  // Resume an open request for this exact action instead of opening a second
  // one: closing the modal does not cancel a request, and an approved one
  // should complete, not re-ask.
  useEffect(() => {
    // A caller that names the request (or says there is none) has already
    // resolved this from server data; matching by target would be a guess.
    if (resumeRequestId !== undefined) return;
    if (requestId !== null || !openRequests.data || !user) return;
    const open = findOpenRequest(
      openRequests.data.filter(request => !dismissedIds.includes(request.id)),
      { operation, target_id: targetId, requester_id: user.sub },
      new Date().toISOString()
    );
    if (open) setRequestId(open.id);
  }, [resumeRequestId, requestId, openRequests.data, user, operation, targetId, dismissedIds]);

  const request = detail.data ?? null;
  const status = request ? effectiveStatus(request, nowIso) : null;
  const approvalToken = status === 'APPROVED' ? request?.approval_token : undefined;

  // Complete the action the moment the approval lands.
  useEffect(() => {
    if (!request || status !== 'APPROVED' || !approvalToken || stale) return;
    if (executedFor.current === request.id) return;
    executedFor.current = request.id;
    setExecution('running');
    setExecutionError(null);
    executeRef
      .current({ reason: request.reason, note: request.note, approvalId: approvalToken }, request)
      .then(() => {
        setExecution('done');
        queryClient.invalidateQueries({ queryKey: approvalRequestKeys.all });
        onCompletedRef.current();
        onCloseRef.current();
      })
      .catch((error: unknown) => {
        setExecution('failed');
        setExecutionError(error instanceof Error ? error.message : 'The action failed.');
      });
  }, [request, status, approvalToken, stale, queryClient, attempt]);

  const opening = createMutation.isPending || openMutation.isPending;
  const busy = execution === 'running' || opening || withdrawMutation.isPending;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && execution !== 'running') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [execution]);

  const submitRequest = (reason: string, note: string | undefined) => {
    if (openRequest) {
      openMutation.mutate({ reason, note }, { onSuccess: created => setRequestId(created.id) });
      return;
    }
    if (operation === 'publishContentRevision') return;
    createMutation.mutate(
      { operation, target_id: targetId, target_label: targetLabel, reason, note },
      { onSuccess: created => setRequestId(created.id) }
    );
  };

  const startOver = () => {
    if (request) setDismissedIds(ids => [...ids, request.id]);
    executedFor.current = null;
    setExecution('idle');
    setExecutionError(null);
    setRequestId(null);
  };

  const retryExecution = () => {
    if (!request || !approvalToken) return;
    executedFor.current = null;
    setExecution('idle');
    setExecutionError(null);
    // Clearing the guard and bumping the attempt re-runs the completion
    // effect; a token the server already consumed comes back as 403 and lands
    // in the failed state again, where "Request again" is the way out.
    setAttempt(count => count + 1);
  };

  const withdraw = () => {
    if (!request) return;
    withdrawMutation.mutate({ id: request.id }, { onSuccess: startOver });
  };

  const checkingForOpenRequest =
    resumeRequestId === undefined && requestId === null && openRequests.isLoading;
  const staleAndOpen = stale && (status === 'PENDING' || status === 'APPROVED');
  // A revision's request can be somebody else's: they may look, not act.
  const isRequester = request !== null && user?.sub === request.requester_id;
  const canRequestAgain = operation !== 'publishContentRevision' || openRequest !== undefined;
  const loadingRequest = requestId !== null && !request && detail.isLoading;
  const countdown = request ? expiryCountdown(request, nowIso) : null;

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={heading}
        className="glass-card w-full max-w-lg max-h-[90vh] overflow-y-auto animate-spring-in"
      >
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                {heading}
              </h3>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <StatusChip label={operationInfo.label} tone={operationInfo.tone} />
                {status && (
                  <StatusChip label={statusChip(status).label} tone={statusChip(status).tone} />
                )}
                {countdown && <StatusChip label={countdown.label} tone={countdown.tone} />}
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={execution === 'running'}
              className="text-gray-text hover:text-white disabled:opacity-50"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="rounded-lg border border-gray-border bg-dark-lighter px-4 py-3">
            <p className="text-xs text-gray-text uppercase tracking-wide mb-1">Target</p>
            <p className="text-sm text-white font-mono break-all">{targetLabel}</p>
          </div>

          {description && <p className="text-sm text-gray-text">{description}</p>}

          {checkingForOpenRequest || loadingRequest ? (
            <div className="flex items-center gap-3 text-sm text-gray-text py-4">
              <RefreshCw className="w-4 h-4 animate-spin text-primary" />
              <span>Checking for an open request…</span>
            </div>
          ) : requestId === null ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-text">
                This action needs a second operator. Pick a reason and a request goes to the
                Approvals queue. Once another operator approves it, this console completes the
                action for you. Nobody handles a token.
              </p>
              <ReasonActionBar
                reasons={REASONS_BY_OPERATION[operation]}
                actionLabel="Request approval"
                destructive={destructive}
                isPending={opening}
                notePlaceholder="What changes and why. The approver reads this."
                onSubmit={submitRequest}
              />
            </div>
          ) : !request ? (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400">
              {detail.error instanceof Error
                ? detail.error.message
                : 'The request could not be loaded.'}
            </div>
          ) : staleAndOpen ? (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <p className="text-sm font-medium text-red-400 mb-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  The live route changed after this revision was proposed
                </p>
                <p className="text-sm text-white">
                  It can no longer be published, and an approval of it is void. Your text is still
                  stored: rebase it onto the current version and request approval again.
                </p>
              </div>
              <div className="flex items-center justify-end gap-3">
                <button onClick={onClose} className="btn-secondary">
                  Close
                </button>
                {onRevise && (
                  <button onClick={onRevise} className="btn-primary">
                    Rebase in editor
                  </button>
                )}
              </div>
            </div>
          ) : status === 'PENDING' ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5 animate-pulse" />
                <div className="text-sm">
                  <p className="text-white font-medium">Awaiting a second operator</p>
                  <p className="text-gray-text mt-1">
                    Requested{' '}
                    {formatDistanceToNow(new Date(request.requested_at), { addSuffix: true })}.
                    Another operator decides it on the Approvals page; this window completes the
                    action as soon as they approve. You can close it and come back, the request
                    stays open.
                  </p>
                </div>
              </div>
              <RequestSummary reason={request.reason} note={request.note} />
              <div className="flex items-center justify-end gap-3">
                <button onClick={onClose} className="btn-secondary" disabled={busy}>
                  Close
                </button>
                {isRequester && (
                  <button
                    onClick={withdraw}
                    disabled={busy}
                    className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50"
                  >
                    {withdrawMutation.isPending ? 'Withdrawing…' : 'Withdraw request'}
                  </button>
                )}
              </div>
            </div>
          ) : status === 'APPROVED' ? (
            <div className="space-y-4">
              {execution === 'failed' ? (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <p className="text-sm font-medium text-red-400 mb-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    The action failed after approval
                  </p>
                  <p className="text-sm text-white break-words">{executionError}</p>
                </div>
              ) : !approvalToken ? (
                <p className="text-sm text-gray-text">
                  Approved, but the completion token is only issued to the operator who requested
                  it. Complete this from their console.
                </p>
              ) : (
                <div className="flex items-start gap-3 text-sm">
                  <RefreshCw className="w-5 h-5 text-primary animate-spin flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">
                      Approved by{' '}
                      <span className="font-mono text-xs break-all">{request.decider_id}</span>
                    </p>
                    <p className="text-gray-text mt-1">Completing the action…</p>
                  </div>
                </div>
              )}
              {request.decision_note && (
                <p className="text-sm text-gray-text">
                  Approver note: <span className="text-white">{request.decision_note}</span>
                </p>
              )}
              {execution === 'failed' && (
                <div className="flex items-center justify-end gap-3">
                  <button onClick={startOver} className="btn-secondary">
                    Request again
                  </button>
                  <button onClick={retryExecution} className="btn-primary">
                    Try again
                  </button>
                </div>
              )}
            </div>
          ) : status === 'COMPLETED' ? (
            <div className="space-y-4">
              <p className="text-sm text-white flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                Completed
                {request.completed_at &&
                  ` ${formatDistanceToNow(new Date(request.completed_at), { addSuffix: true })}`}
                .
              </p>
              <div className="flex justify-end">
                <button onClick={onClose} className="btn-secondary">
                  Close
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-dark-lighter border border-gray-border text-sm">
                <p className="text-white font-medium">
                  {status === 'REJECTED'
                    ? 'A second operator rejected this request.'
                    : status === 'WITHDRAWN'
                      ? 'This request was withdrawn.'
                      : 'This request expired before it was completed.'}
                </p>
                {request.decider_id && (
                  <p className="text-gray-text mt-1">
                    Decided by{' '}
                    <span className="font-mono text-xs text-white break-all">
                      {request.decider_id}
                    </span>
                    {request.decided_at &&
                      ` ${formatDistanceToNow(new Date(request.decided_at), { addSuffix: true })}`}
                  </p>
                )}
                {request.decision_note && (
                  <p className="text-gray-text mt-1">
                    Note: <span className="text-white">{request.decision_note}</span>
                  </p>
                )}
              </div>
              <div className="flex items-center justify-end gap-3">
                <button onClick={onClose} className="btn-secondary">
                  Close
                </button>
                {onRevise && (
                  <button onClick={onRevise} className="btn-secondary">
                    Revise in editor
                  </button>
                )}
                {canRequestAgain && (
                  <button onClick={startOver} className="btn-primary">
                    Request again
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RequestSummary({ reason, note }: { reason: string; note?: string }) {
  return (
    <div className="rounded-lg border border-gray-border divide-y divide-gray-border/50 text-sm">
      <div className="px-3 py-2 flex gap-3">
        <span className="text-xs text-gray-text font-mono w-20 flex-shrink-0">reason</span>
        <span className="text-white font-mono break-all">{reason}</span>
      </div>
      {note && (
        <div className="px-3 py-2 flex gap-3">
          <span className="text-xs text-gray-text font-mono w-20 flex-shrink-0">note</span>
          <span className="text-white break-words whitespace-pre-wrap">{note}</span>
        </div>
      )}
    </div>
  );
}
